import { ChevronDown, ChevronRight, FolderOpen } from 'lucide-react'
import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getProjectsByWorkspace, listAnalysesOverview } from '@/client/sdk.gen'
import type { ProjectInfo } from '@/client/types.gen'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useAuth, useDataRefresh, useWorkspace } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

export interface SidebarProjectsDropdownProps {
	currentPage?: string
	collapsed?: boolean
}

const ACTIVE_PAGES = new Set(['projects', 'projectDetail', 'analysisDetail'])
const SIDEBAR_PROJECT_LIMIT = 5
const PROJECTS_CACHE_TTL_MS = 30_000

interface AnalysisDropdownProps {
	projectId: string
	isOpen: boolean
}

/**
 * Helper type for objects that can be ordered by `created_at`.
 */
interface HasCreatedAt {
	created_at?: string | null
}

/**
 * Sorts projects and analyses by creation time, and only returns a specified amount.
 */
function sortByCreationTime<T extends HasCreatedAt>(items: T[], amount: number = 3): T[] {
	return (
		items
			.slice()
			// Sort by creation time
			.sort((firstItem, secondItem) => {
				const firstCreatedTime = firstItem.created_at ? new Date(firstItem.created_at).getTime() : 0
				const secondCreatedTime = secondItem.created_at ? new Date(secondItem.created_at).getTime() : 0
				return secondCreatedTime - firstCreatedTime
			})
			// Only return the specified amount of items
			.slice(0, amount)
	)
}

/**
 * Renders a list of the most recently updated analyses for a project.
 * Fetches analyses lazily when the section is opened and the version changes.
 * @param projectId - The ID of the project to fetch analyses for.
 * @param isOpen - Whether the section is open.
 * @param version - The version of the analyses to fetch.
 * @returns A list of the most recently updated analyses for the project.
 */
function AnalysisDropdown({ projectId, isOpen }: AnalysisDropdownProps) {
	const [isLoadingAnalyses, setIsLoadingAnalyses] = React.useState(false)
	const { analysesByProject, setAnalysesForProject } = useDataRefresh()
	const { t } = useTranslation()
	const analyses = analysesByProject[projectId] ?? null

	React.useEffect(() => {
		if (!isOpen || analyses !== null) return

		let isCancelled = false
		setIsLoadingAnalyses(true)

		// Fetch all analyses for the project
		listAnalysesOverview({ path: { project_id: projectId }, query: { skip: 0, limit: 100 } })
			.then((result) => {
				if (!isCancelled) {
					// Sort analyses by creation time so the most recent analyses appear at the top
					setAnalysesForProject(projectId, sortByCreationTime(result.data?.analyses ?? [], 3))
				}
			})
			.catch(() => {
				if (!isCancelled) setAnalysesForProject(projectId, [])
			})
			.finally(() => {
				if (!isCancelled) setIsLoadingAnalyses(false)
			})

		return () => {
			isCancelled = true
		}
	}, [isOpen, analyses, projectId, setAnalysesForProject])

	// Loading state
	if (isLoadingAnalyses) {
		return (
			<div className="pl-8 py-1">
				<div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
			</div>
		)
	}

	// No analyses found
	if (analyses !== null && analyses.length === 0) {
		return (
			<p className="pl-8 py-1 text-xs text-gray-400">{t('sidebar.noAnalyses') || 'No analyses'}</p>
		)
	}

	// Render the analyses
	return (
		<>
			{(analyses ?? []).map((analysis) => (
				<Link
					key={analysis.id}
					to={`/projects/${projectId}/analyses/${analysis.id}`}
					className="flex items-center gap-2 pl-8 pr-2 py-1.5 text-xs text-gray-600 rounded-lg hover:bg-gray-100 hover:text-gray-900 transition-colors truncate"
				>
					<span className="truncate">{analysis.name}</span>
				</Link>
			))}
		</>
	)
}

interface ProjectItemProps {
	project: ProjectInfo
}

/**
 * Persists a boolean value in `sessionStorage` and restores it when the component mounts.
 *
 * @param key - Storage key used in `sessionStorage`.
 * @param defaultValue - Fallback value when nothing is stored or parsing fails.
 * @returns Tuple with the current value and a setter that also persists.
 */
function useSessionState(key: string, defaultValue: boolean): [boolean, (v: boolean) => void] {
	const [storedValue, setStoredValue] = React.useState<boolean>(() => {
		try {
			const storedRawValue = sessionStorage.getItem(key)
			return storedRawValue !== null ? (JSON.parse(storedRawValue) as boolean) : defaultValue
		} catch {
			return defaultValue
		}
	})

	const setAndPersist = React.useCallback(
		(nextValue: boolean) => {
			setStoredValue(nextValue)
			try {
				sessionStorage.setItem(key, JSON.stringify(nextValue))
			} catch {
				// ignore storage errors
			}
		},
		[key]
	)

	return [storedValue, setAndPersist]
}

/**
 * Renders a single project row with a collapsible section that shows recent analyses.
 *
 * @param project - Project information to display.
 * @param analysisVersion - Version number used to refetch analyses when it changes.
 * @returns A single project row with a collapsible section that shows recent analyses.
 */
function ProjectItem({ project }: ProjectItemProps) {
	const [isAnalysisDropdownOpen, setIsAnalysisDropdownOpen] = useSessionState(
		`sidebar:analysis-open:${project.id}`,
		false
	)
	const { t } = useTranslation()

	return (
		<Collapsible
			open={isAnalysisDropdownOpen}
			onOpenChange={setIsAnalysisDropdownOpen}
			className="w-full overflow-hidden"
		>
			<div className="flex items-center rounded-lg hover:bg-gray-100 group min-w-0">
				<Link
					to={`/projects/${project.id}`}
					className="flex-1 min-w-0 flex items-center gap-2 pl-4 pr-1 py-1.5 text-sm text-gray-700"
				>
					<span className="truncate">{project.name}</span>
				</Link>

				<CollapsibleTrigger asChild>
					<button
						type="button"
						aria-label={
							isAnalysisDropdownOpen
								? t('navigation.collapseAnalyses') || 'Collapse analyses'
								: t('navigation.expandAnalyses') || 'Expand analyses'
						}
						className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-200 hover:scale-105 active:scale-100 transition-all duration-300 ease-in-out"
					>
						<ChevronRight
							className={cn(
								'w-3 h-3 transition-transform duration-200',
								isAnalysisDropdownOpen && 'rotate-90'
							)}
						/>
					</button>
				</CollapsibleTrigger>
			</div>

			<CollapsibleContent>
				<AnalysisDropdown projectId={project.id} isOpen={isAnalysisDropdownOpen} />
			</CollapsibleContent>
		</Collapsible>
	)
}

export function SidebarProjectsDropdown({ currentPage, collapsed }: SidebarProjectsDropdownProps) {
	const [isProjectsDropdownOpen, setIsProjectsDropdownOpen] = useSessionState(
		'sidebar:projects-open',
		false
	)
	const [isLoadingProjects, setIsLoadingProjects] = React.useState(false)
	const { user } = useAuth()
	const { selectedWorkspaceId } = useWorkspace()
	const { projectsByWorkspace, setProjectsForWorkspace } = useDataRefresh()
	const { t } = useTranslation()
	const navigate = useNavigate()

	const userWorkspaceId = (user as { workspace_id?: string } | null)?.workspace_id
	const workspaceId = selectedWorkspaceId ?? userWorkspaceId

	const isActive = currentPage !== undefined && ACTIVE_PAGES.has(currentPage)
	const projectsData = workspaceId ? (projectsByWorkspace[workspaceId] ?? null) : null
	const projects = projectsData?.projects ?? null
	const totalProjects = projectsData?.totalCount ?? null
	const isProjectsCacheStale =
		projectsData !== null && Date.now() - projectsData.fetchedAtMs > PROJECTS_CACHE_TTL_MS

	// Fetch projects whenever the dropdown is open and data is missing or stale.
	// If stale data exists, keep rendering it while revalidating in the background.
	React.useEffect(() => {
		if (!isProjectsDropdownOpen || !workspaceId) return
		if (projects !== null && !isProjectsCacheStale) return

		let isCancelled = false
		const hasCachedProjects = projects !== null
		if (!hasCachedProjects) {
			setIsLoadingProjects(true)
		}

		getProjectsByWorkspace({
			path: { workspace_id: workspaceId },
			query: { limit: SIDEBAR_PROJECT_LIMIT },
		})
			.then((response) => {
				if (!isCancelled) {
					setProjectsForWorkspace(
						workspaceId,
						response.data?.projects ?? [],
						response.data?.total_count ?? null
					)
				}
			})
			.catch(() => {
				if (!isCancelled) setProjectsForWorkspace(workspaceId, [], 0)
			})
			.finally(() => {
				if (!isCancelled && !hasCachedProjects) setIsLoadingProjects(false)
			})

		return () => {
			isCancelled = true
		}
	}, [isProjectsDropdownOpen, isProjectsCacheStale, projects, workspaceId, setProjectsForWorkspace])

	// When sidebar is collapsed, render a simple icon button that navigates to /projects
	if (collapsed) {
		return (
			<button
				type="button"
				onClick={() => navigate('/projects')}
				title={t('navigation.projects')}
				className={cn(
					'flex gap-2 p-2 rounded-lg transition-all cursor-pointer active:scale-100 hover:scale-105 duration-300 ease-in-out',
					isActive
						? 'bg-purple-100 border border-purple-200 text-purple-700'
						: 'hover:bg-gray-100 hover:text-gray-900'
				)}
				aria-current={isActive ? 'page' : undefined}
			>
				<FolderOpen className="w-5 h-5 backface-hidden flex-shrink-0" />
			</button>
		)
	}

	return (
		<Collapsible open={isProjectsDropdownOpen} onOpenChange={setIsProjectsDropdownOpen}>
			{/* Original navigation element */}
			<div
				className={cn(
					'flex items-center rounded-lg transition-all duration-300 ease-in-out',
					isActive
						? 'bg-purple-100 border border-purple-200 text-purple-700'
						: 'hover:bg-gray-100 hover:text-gray-900'
				)}
			>
				<Link
					to="/projects"
					className="flex flex-1 items-center gap-2 p-2 truncate cursor-pointer"
					aria-current={isActive ? 'page' : undefined}
				>
					<FolderOpen className="w-5 h-5 backface-hidden flex-shrink-0" />
					<span className="flex-1 text-left truncate">{t('navigation.projects')}</span>
				</Link>

				<CollapsibleTrigger asChild>
					<button
						type="button"
						aria-label={
							isProjectsDropdownOpen ? t('navigation.collapseProjects') : t('navigation.expandProjects')
						}
						className="flex-shrink-0 p-2 rounded-md cursor-pointer hover:scale-110 hover:bg-gray-200 hover:text-gray-900 hover:shadow-sm active:scale-100 active:bg-gray-300 transition-all duration-300 ease-in-out"
					>
						<ChevronDown
							className={cn(
								'w-4 h-4 transition-transform duration-200',
								isProjectsDropdownOpen && 'rotate-180'
							)}
						/>
					</button>
				</CollapsibleTrigger>
			</div>

			{/* Project list */}
			<CollapsibleContent>
				<div className="mt-1 flex flex-col gap-0.5">
					{isLoadingProjects &&
						[1, 2, 3].map((skeletonIndex) => (
							<div key={skeletonIndex} className="flex items-center gap-2 pl-4 pr-2 py-1.5">
								<div className="h-3 flex-1 bg-gray-200 rounded animate-pulse" />
							</div>
						))}

					{!isLoadingProjects && projects !== null && projects.length === 0 && (
						<p className="pl-4 py-1.5 text-xs text-gray-400">
							{t('sidebar.noProjects') || 'No projects yet'}
						</p>
					)}

					{!isLoadingProjects &&
						(projects ?? []).map((project) => <ProjectItem key={project.id} project={project} />)}

					{!isLoadingProjects &&
						projects !== null &&
						totalProjects !== null &&
						totalProjects > SIDEBAR_PROJECT_LIMIT && (
							<Link
								to="/projects"
								className="pl-4 py-1.5 text-xs font-medium text-purple-600 hover:text-purple-700"
							>
								{t('sidebar.allProjects') || 'All projects'}
							</Link>
						)}
				</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
